<template>
    <div class="chatbot-page"
        :class="{
            'horizontal-answer-list': mustShowHorizontalAnswerList,
            'vertical-answer-list': !mustShowHorizontalAnswerList
        }"
         v-touch:tap="hideAnswerEditIcon">

        <div class="chatbot-page-header">
            <header-bar @openDocPopup="openDocPopup"></header-bar>
        </div>

        <div class="chatbot-page-content-wrapper"><!-- Wrapper for the whole page content, excepting the header. -->
            <div class="chatbot-page-content animate-hide-answer-list"><!-- Whole page content (excepting the header) -->

                <div class="chatbot-conversation-msg-list"><!-- Wrapper for the list of all messages ("replies") already posted in the chat. It includes both received (questions, etc.) and sent (answers) messages. -->

                    <!-- https://vuejs.org/v2/guide/transitions.html#Staggering-List-Transitions -->
                    <transition-group v-on:enter="animateAppendConversationMsg"
                                      v-on:before-enter="PfChatbotPlugin.animateBeforeAppendConversationMsg">
                        <div class="conversation-msg"
                             v-for="(conversationMessage, index) in getConversationMessages"
                             :key="index"
                             :data-index="index"
                             :class="{
                                 'received-message-item-container': (conversationMessage.type === getConversationMessageTypes.RECEIVED),
                                 'sent-message-item-container': (conversationMessage.type === getConversationMessageTypes.SENT),
                                 'conversation-msg--prepared-for-sticky': (conversationMessage.type === getConversationMessageTypes.RECEIVED && !isConvMsgPrecedingASentMsg(index, getConversationMessages)),
                                 'conversation-msg--next-msg-same-type': (isNextConvMsgSameType(index, getConversationMessages)),
                                 'conversation-msg--last': (index === getConversationMessages.length - 1)
                             }">
                            <message-received
                                @openDocPopup="openDocPopup"
                                v-if="conversationMessage.type === getConversationMessageTypes.RECEIVED && conversationMessage.text.length > 0"
                                :msgText="conversationMessage.text">
                            </message-received>
                            <message-sent
                                v-else-if="conversationMessage.type === getConversationMessageTypes.SENT && conversationMessage.text.length > 0"
                                :msgText="conversationMessage.text"
                                :question-code="conversationMessage.questionCode"
                                :convMsgObj="conversationMessage"
                                @onClickEditAnswer="handleClickEditAnswer(conversationMessage)">
                            </message-sent>
                        </div>
                    </transition-group>


                    <!-- ---- Sticky messages clones ---- -->
                    <div class="container-last-conv-messages-clones">
                        <div class="conversation-msg conversation-msg--clone"
                             v-for="(conversationMessage, index) in lastConsecutiveReceivedConvMessages"
                             :key="index"
                             :data-index="index"
                             :class="{
                                    'received-message-item-container': (conversationMessage.type === getConversationMessageTypes.RECEIVED),
                                    'sent-message-item-container': (conversationMessage.type === getConversationMessageTypes.SENT),
                                    'conversation-msg--next-msg-same-type': (isNextConvMsgSameType(index, lastConsecutiveReceivedConvMessages)),
                                    'conversation-msg--last': (index === lastConsecutiveReceivedConvMessages.length - 1)
                             }">
                            <message-received
                                @openDocPopup="openDocPopup"
                                v-if="conversationMessage.type === getConversationMessageTypes.RECEIVED && conversationMessage.text.length > 0"
                                :msgText="conversationMessage.text">
                            </message-received>
                            <message-sent
                                v-else-if="conversationMessage.type === getConversationMessageTypes.SENT && conversationMessage.text.length > 0"
                                :msgText="conversationMessage.text"
                                :question-code="conversationMessage.questionCode">
                            </message-sent>
                        </div>
                    </div>
                    <!-- ---- END Sticky messages clones ---- -->

                </div>


                <answer-list></answer-list><!-- Wrapper for area where the user can see the answer options buttons, inputs, search for symptoms, other buttons etc. -->

                <transition name="more-content-fade">
                    <a v-show="getMoreContentAvailable" class="btn-scroll-down" @click="scrollToBottom()" style="position: fixed;">
                        <more-content class="more-content-icon"></more-content>
                    </a>
                </transition>

            </div>
            <div class="cookies-display">
                <detect-browser-cookie></detect-browser-cookie>
                <accept-cookie></accept-cookie>
            </div>
        </div>


        <!-- Doc Popup -->
        <doc-popup></doc-popup>

        <!-- URGENT POPUP -->
        <chat-bot-urgent-popup v-if="isKSB !== 'PF_CHATBOT_STUDY'"></chat-bot-urgent-popup>
        <edit-answer-popup></edit-answer-popup>

    </div>
</template>

<!-- ################################################################## -->

<script>
import {mapGetters} from 'vuex';
import MessageReceived from './MessageReceived';
import MessageSent from './MessageSent';
import AnswerList from './AnswerList';
import HeaderBar from "../../../pathfinder/components/shared/HeaderBar";
import ChatBotUrgentPopup from "./ChatBotUrgentPopup";
import MoreContent from "../../../common/components/svg/shared/MoreContentIcon";
import EditAnswerPopup from "./EditAnswerPopup";
import DetectBrowserCookie from "../../../common/components/cookies/DetectBrowserCookie";
import DocPopup from "../../../common/components/shared/DocPopup";
import AcceptCookie from "../../../professional/components/cookies/AcceptCookie.vue";

// This class will be added on <html> tag when current route is pointing to this component.
const CLASS_CHATBOT_ROOT = "root-pf-chatbot";

// This class will be added on <html> tag when the device is iPad, iPhone or iPod. In Safari, if "Request Desktop Website"
// is enabled, this class won't be added because in this case Safari's userAgent will be similar to the one on Mac,
// even on iPad. For this case, use CLASS_MAC and CLASS_TOUCH_SUPPORT together.
const CLASS_IOS = "is-apple-mobile";

// This class will be added on <html> tag when the userAgent contains "Macintosh". This happens even on iPad Safari
// with "Request desktop site" option enabled.
const CLASS_MAC = "is-mac-platform";

// This class will be added on <html> tag when the device has touch support.
const CLASS_TOUCH_SUPPORT = "has-touch-support";


export default {
        name: 'ChatBotPage',
        components: {
            AcceptCookie,
            DocPopup,
            HeaderBar,
            MessageReceived,
            MessageSent,
            AnswerList,
            ChatBotUrgentPopup,
            MoreContent,
            EditAnswerPopup,
            DetectBrowserCookie
        },

        data() {
            return { };
        },
        computed: {
            ...mapGetters({
                getSelectedProductCode: 'ProductListStore/getSelectedProductCode',
                getConversationMessages: 'ChatBotStore/getConversationMessages',
                getConversationMessageTypes: 'ChatBotStore/getConversationMessageTypes',
                getCurrentAssessmentStatus: 'ChatBotStore/getCurrentAssessmentStatus',
                getAssessmentStatuses: 'ChatBotStore/getAssessmentStatuses',
                isKSB: 'TranslationStore/getAppTypeName',
                getMoreContentAvailable: 'ChatBotStore/getMoreContentAvailable',
                getAppPropertyValue: 'TranslationStore/getAppPropertyValue',
            }),

            mustShowHorizontalAnswerList() {
                return false;
            },

            lastConversationMessage() {
                return this.getConversationMessages[this.getConversationMessages.length-1];
            },

            /**
             * Find last consecutive conversation messages that have type=RECEIVED.
             * @return {Array} - The list of all received messages that came after the last SENT message. Usually (but not always) the list will contain only one item.
             */
            lastConsecutiveReceivedConvMessages() {
                let resConvMessagesArr = [];

                if (this.getConversationMessages.length > 0) {
                    // Loop through conversation messages, starting with the last one.
                    for (let i=(this.getConversationMessages.length-1); i>=0; i--) {
                        if (this.getConversationMessages[i].type === this.getConversationMessageTypes.RECEIVED) {
                            resConvMessagesArr.unshift(this.getConversationMessages[i]);
                        }
                        else {
                            // We arrived to a message that has type different than RECEIVED,
                            // so we don't need to search anymore (because we only need consecutive received messages).
                            break;
                        }
                    }
                }

                return resConvMessagesArr;
            },

            dc() {
                return this.$route.query.dc;
            }
        },

        beforeCreate() {
            // Reset stores.
            this.$store.commit('ChatBotStore/resetStore');

            // Because the store is reset we need to update the drSmassPersonas
            this.$store.dispatch('ChatBotStore/updateDrSmassPersonas');

            sessionStorage.setItem('smass-home-route', "PF_ChatBotPage"); // This will help us to know HOME menu button destination.

            sessionStorage.removeItem("proAssessmentToken");
        },

        created () {

            // Store data container param from URL (user can be authenticated or not)
            this.submitDataContainer();

            // Load product list data from server.
            this.$store.dispatch('ChatBotStore/loadProductList');
            this.$store.commit('ChatBotStore/setCurrentAssessmentStatus', this.getAssessmentStatuses.PRODUCT_LIST);

            window.addEventListener('scroll', this.handleConversationScroll);
            window.addEventListener('resize', this.handleWindowResize);

            // Add iOS specific class on body element.
            let htmlElem = document.querySelector('html');
            let isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
            if (isIOS && !htmlElem.classList.contains(CLASS_IOS)) {
                htmlElem.classList.add(CLASS_IOS);
            }

            let isMacLikePlatform = /Macintosh/.test(navigator.userAgent) && /Mac/i.test(navigator.platform) && !window.MSStream;
            if (isMacLikePlatform && !htmlElem.classList.contains(CLASS_MAC)) {
                htmlElem.classList.add(CLASS_MAC);
            }

            let hasTouchSupport = ('ontouchend' in document) ? true : false; // && navigator.maxTouchPoints > 0
            if (hasTouchSupport && !htmlElem.classList.contains(CLASS_TOUCH_SUPPORT)) {
                htmlElem.classList.add(CLASS_TOUCH_SUPPORT);
            }

            // iOS Safari: Start each Assessment with Scroll to 0
            window.scrollTo(0, 0);

        },

        mounted() {
            // Add chatbot identifier class on body element.
            let htmlElem = document.querySelector('html');
            htmlElem.classList.add(CLASS_CHATBOT_ROOT);

        },
        destroyed() {
            // // Remove scroll handler.
            window.removeEventListener('scroll', this.handleConversationScroll);
            window.removeEventListener('resize', this.handleWindowResize);

            // Remove chatbot identifier class on body element.
            let htmlElem = document.querySelector('html');
            htmlElem.classList.remove(CLASS_CHATBOT_ROOT);
        },
        methods: {

            animateAppendConversationMsg(crtConvMsgElem) {

                this.PfChatbotPlugin.animateAppendConversationMsg(crtConvMsgElem).then(() => {

                    let windowHeight = window.innerHeight;
                    let pageHeaderHeight = document.querySelector('.chatbot-page-header nav').offsetHeight;
                    let answerListHeight = document.querySelector('.chatbot-answer-list').offsetHeight;

                    let convList = document.querySelector('.chatbot-conversation-msg-list');

                    let convListCrtPaddingTop = parseInt(window.getComputedStyle(convList).getPropertyValue('padding-top'));
                    let convListCrtPaddingBottom = parseInt(window.getComputedStyle(convList).getPropertyValue('padding-bottom'));
                    let convListContentHeight = convList.clientHeight - convListCrtPaddingTop - convListCrtPaddingBottom;

                    let newPaddingBottom = windowHeight - pageHeaderHeight - convListContentHeight - answerListHeight;

                    if (newPaddingBottom < 0) {
                        newPaddingBottom = 0;
                    }

                    convList.style.paddingTop = '0px';
                    convList.style.paddingBottom = newPaddingBottom + 'px';

                });
            },

            handleWindowResize(event) {

                // ------------ "MORE CONTENT" ICON ------------

                this.PfChatbotPlugin.refreshIsMoreContentAvailable(true);
            },

            handleConversationScroll(event) {

                // ------------ STICKY LAST QUESTION(S) ------------

                this.refreshStickyQuestions();

                // ------------ "MORE CONTENT" ICON ------------

                this.PfChatbotPlugin.refreshIsMoreContentAvailable(true);

            },

            scrollToBottom() {

                let scrollHeight = Math.max(
                    document.body.scrollHeight,
                    document.body.offsetHeight,
                    document.documentElement.clientHeight,
                    document.documentElement.scrollHeight,
                    document.documentElement.offsetHeight
                );
                let maxScrollTop = scrollHeight - window.innerHeight;

                this.UtilitiesPlugin.smoothScrollTo(maxScrollTop);

            },

            isNextConvMsgSameType(convMsgIndexInConvMsgList, convMsgList) {

                let isSameType = false;
                let msgObj = convMsgList[convMsgIndexInConvMsgList];
                let nextMsgObj = convMsgList[(convMsgIndexInConvMsgList+1)];

                if (msgObj && nextMsgObj && msgObj.type === nextMsgObj.type) {
                    isSameType = true;
                }

                return isSameType;
            },


            // -------------------------------------------------
            // ------------ STICKY LAST QUESTION(S) ------------
            // -------------------------------------------------

            /**
             * In a given conversation message list, checks if there is any SENT conversation message after a specific message (passed in param as array index).
             * @param convMsgIndexInConvMsgList {Number} - Array index.
             * @param convMsgList {Array} - List of conversation messages.
             * @return {Boolean} - True if there is one or more SENT message(s) after the given message, false otherwise.
             */
            isConvMsgPrecedingASentMsg(convMsgIndexInConvMsgList, convMsgList) {

                let isFoundSentMsg = false;
                if (convMsgList.length > 0) {
                    // Loop through conversation messages, starting with the last one.
                    for (let i=(convMsgList.length-1); i>=(convMsgIndexInConvMsgList+1); i--) {
                        if (convMsgList[i].type === this.getConversationMessageTypes.SENT) {
                            isFoundSentMsg = true;
                            break;
                        }
                    }
                }
                return isFoundSentMsg;
            },


            /**
             * Checks if there is any conversation messages that has to become sticky or not sticky and sets them as needed.
             * @return {Void}
             */
            refreshStickyQuestions() {

                let elemFirstConvMsgToBeSticky = document.querySelector('.chatbot-conversation-msg-list .conversation-msg--prepared-for-sticky');
                let elemContainerLastConvMsgClones = document.querySelector('.chatbot-conversation-msg-list .container-last-conv-messages-clones');

                if (!elemFirstConvMsgToBeSticky || !elemContainerLastConvMsgClones) {
                    return;
                }

                // Compute how many pixels are between the top edge of the conversation list and the top edge of the conversation message (or the first one, if there are more of them) that must stay sticky.
                let lastConvMsgMaxDistanceToHeader = elemFirstConvMsgToBeSticky.offsetTop;
                let crtScrollTop = window.scrollY || document.documentElement.scrollTop;

                if (crtScrollTop > lastConvMsgMaxDistanceToHeader) {
                    // Enable sticky question.
                    elemContainerLastConvMsgClones.classList.add("is-sticky-active");
                }
                else {
                    // Disable sticky question.
                    elemContainerLastConvMsgClones.classList.remove("is-sticky-active");
                }
            },


            handleClickEditAnswer(conversationMessage) {

                if (conversationMessage.status === this.getAssessmentStatuses.RELATED_SYMPTOMS) {

                    this.$store.commit("ChatBotStore/setCrtEditingQuestionData", null);
                    let promiseLoadRelatedSymptom = this.$store.dispatch("ChatBotStore/loadRelatedSymptomList");

                    promiseLoadRelatedSymptom.then(() => {
                        // Show "Edit Answer" popup.
                        this.$store.commit("ChatBotStore/setMustShowEditAnswerPopup", true);
                        // Disable background scrolling.
                        document.querySelector('body').classList.add("hide-scroll");
                    });
                }
                else if (conversationMessage.status === this.getAssessmentStatuses.CHAPTER_TREE) {

                    this.$store.dispatch("ChatBotStore/loadChapterTree", {
                        "edit": true
                    }).then((response) => {
                        // Show "Choose Main Symptom" popup.
                        this.$store.commit("ChatBotStore/setMustShowChooseMainSymptomPopup", true);
                        let rootElement = document.getElementsByClassName('root-pf-chatbot')[0];
                        rootElement.classList.add('hide-scroll');
                    });

                }
                else if (conversationMessage.status === this.getAssessmentStatuses.NO_OTHER_SYMPTOMS) {

                    this.$store.dispatch("ChatBotStore/buildNoOtherSymptomQuestionObjForEdit", {
                        "status": this.getAssessmentStatuses.NO_OTHER_SYMPTOMS
                    });
                    // Show "Edit Answer" popup.
                    this.$store.commit("ChatBotStore/setMustShowEditAnswerPopup", true);

                }
                else {
                    let questionCode = conversationMessage.questionCode;
                    this.$store.dispatch("ChatBotStore/loadCrtEditingQuestion", questionCode).then((response) => {

                        // Show "Edit Answer" popup.
                        this.$store.commit("ChatBotStore/setMustShowEditAnswerPopup", true);
                        // Disable background scrolling.
                        document.querySelector('body').classList.add("hide-scroll");
                    });
                }

            },


            hideAnswerEditIcon(event) {
                if(event.target.classList.contains("sent-message-item" ) || event.target.classList.contains("edit-answer-btn-icon" )) {
                    return
                }
                else {
                    let allTouchTapClasses = document.getElementsByClassName('shown-by-touch-tap');
                    for(let i = 0; i < allTouchTapClasses.length; i++) {
                        allTouchTapClasses[i].classList.remove('shown-by-touch-tap');
                    }
                }
            },

            openDocPopup(code) {
                let data = {
                    title: 'api.doc.pfi.' + code,
                    url: this.getAppPropertyValue("DOC_PFI_ADDRESS_" + code),
                }

                this.$store.dispatch('DocStore/loadDocData', data)
                    .then(() => {
                        this.PfChatbotPlugin.hideScroll();
                    })
                    .catch(err => {
                        console.log(err);
                    })
            },

            submitDataContainer() {
                if (this.dc != null) {
                    this.$store.dispatch('ChatBotStore/submitDataContainer', this.dc.trim());

                    // Remove "dc" parameter from URL.
                    let routeParams = {...this.$route.query};
                    delete routeParams.dc;
                    this.$router.replace({name: 'PF_ChatBotPage', query: routeParams});
                }
            }

        }

    }
</script>

<!-- ################################################################## -->

<style lang="scss" scoped>

    /* Vue transitions */


    /* Enter and leave animations can use different */
    /* durations and timing functions.              */
    .slide-fade-enter-active {
        transition: all .3s ease;
    }
    .slide-fade-leave-active {
        transition: all .8s cubic-bezier(1.0, 0.5, 0.8, 1.0);
    }
    .slide-fade-enter, .slide-fade-leave-to
        /* .slide-fade-leave-active below version 2.1.8 */ {
        transform: translateX(10px);
        opacity: 0;
    }
    /* END Vue transitions */




    @keyframes magnified{
        0%{
            transform: scale(1,1);
        }
        70%{
            transform: scale(1.05,1.05);
        }
        100%{
            transform: scale(1,1);
        }
    }



</style>



// WEBPACK FOOTER //
// src/pathfinder/components/chatbot/ChatBotPage.vue